import type { Message } from '@nexo/types';
import { useToast, type MenuEntries } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlarmClock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WhenDialog, whenPresets } from '../../components/WhenDialog';
import { api } from '../../lib/api';
import { formatDay, formatTime } from '../../lib/format';
import { errorMessage } from '../../lib/forms';
import { keys } from '../../lib/queries';

/** «Recuérdamelo» on a message: quick choices for the right-click menu, or pick a moment. */
export function useRemindMe(message: Message) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState(false);

  const create = useMutation({
    mutationFn: (remindAt: string) =>
      api.reminders.create({ teamId: message.teamId, messageId: message.id, remindAt }),
    onSuccess: (reminder) => {
      setPicking(false);
      void queryClient.invalidateQueries({ queryKey: keys.reminders(message.teamId) });
      toast({
        title: t('Te lo recordaré: {{day}}, {{time}}', {
          day: formatDay(reminder.remindAt).toLowerCase(),
          time: formatTime(reminder.remindAt),
        }),
        tone: 'success',
      });
    },
    onError: (err) =>
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' }),
  });

  // Times are worked out on click (the menu may have been rendered long before).
  const remindIn = (pick: (presets: ReturnType<typeof whenPresets>) => Date | undefined) => () => {
    const at = pick(whenPresets());
    if (at) create.mutate(at.toISOString());
  };
  const entries: MenuEntries = [
    {
      label: t('Recuérdamelo en 1 hora'),
      icon: <AlarmClock />,
      onSelect: remindIn((presets) => presets[1]?.at),
    },
    {
      label: t('Recuérdamelo mañana a las 9:00'),
      icon: <AlarmClock />,
      onSelect: remindIn((presets) => presets[3]?.at),
    },
    { label: t('Recuérdamelo…'), icon: <AlarmClock />, onSelect: () => setPicking(true) },
  ];

  const element = picking && (
    <WhenDialog
      title={t('¿Cuándo te lo recuerdo?')}
      confirmLabel={t('Guardar recordatorio')}
      loading={create.isPending}
      onPick={(at) => create.mutate(at)}
      onClose={() => setPicking(false)}
    />
  );

  return { entries, element };
}
